import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ScanLine, Loader2, X, Camera, RotateCw, Check, ListMusic, Barcode, Disc3, Crop } from "lucide-react";
import { api } from "@/lib/api";
import { readImageFile, rotateImage, uploadArtwork } from "@/components/riptag/util";
import { ImageCropModal } from "@/components/ImageCropModal";

const SLOTS = [
  ["front", "Front cover"],
  ["back", "Back cover"],
  ["barcode", "Barcode"],
  ["tracklist", "Track listing"],
];

const CROP_ASPECTS = [["Square", 1], ["4:3", 4 / 3], ["Wide", 2.4], ["Tall", 3 / 4]];
const CROP_DEFAULT = { front: 1, back: 4 / 3, barcode: 2.4, tracklist: 3 / 4 };

export const CdScanModal = ({ track, onClose, onApplied }) => {
  const [slots, setSlots] = useState({});
  const [cost, setCost] = useState(null);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [cropSlot, setCropSlot] = useState(null);
  const inputRefs = useRef({});

  useEffect(() => {
    api.get("/rip/scan/settings").then((r) => setCost(r.data)).catch(() => {});
  }, []);

  const pick = async (kind, file) => {
    if (!file) return;
    try {
      const dataUrl = await readImageFile(file);
      setSlots((s) => ({ ...s, [kind]: { dataUrl } }));
    } catch (e) { toast.error(e.message); }
  };

  const rotate = async (kind) => {
    const s = slots[kind];
    if (!s?.dataUrl) return;
    try {
      const rotated = await rotateImage(s.dataUrl);
      setSlots((p) => ({ ...p, [kind]: { dataUrl: rotated } }));
    } catch (e) { toast.error(e.message); }
  };

  const openCrop = async (kind) => {
    const s = slots[kind];
    if (!s?.dataUrl) return;
    try {
      setCropSlot({ kind, blob: await (await fetch(s.dataUrl)).blob() });
    } catch { toast.error("Could not open the crop tool"); }
  };

  const filled = SLOTS.filter(([k]) => slots[k]?.dataUrl);

  const scan = async () => {
    setBusy("scan");
    try {
      const ids = [];
      for (const [kind] of filled) {
        const up = await uploadArtwork(api, slots[kind].dataUrl, kind);
        ids.push(up.art_id);
      }
      const r = await api.post("/rip/scan/identify", {
        image_ids: ids, album_id: track.album_id || "", session_id: track.session_id || "" });
      setResult(r.data);
      const n = r.data.candidates?.length || 0;
      toast.success(r.data.cached ? "Cached scan result — no credits charged"
        : n ? `Scan complete — ${n} possible release${n === 1 ? "" : "s"} found` : "Scan complete — review the extracted text below");
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed — you were not charged"); }
    setBusy("");
  };

  const applyRelease = async (c) => {
    setBusy(c.release_id);
    try {
      const r = track.album_id
        ? await api.post(`/rip/albums/${track.album_id}/apply-mb`, { release_id: c.release_id, confidence: c.confidence })
        : await api.post(`/rip/sessions/${track.session_id}/apply-mb`, { release_id: c.release_id, confidence: c.confidence });
      toast.success(`Applied "${r.data.release}" to ${r.data.updated} tracks — originals kept for undo`);
      onApplied();
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    setBusy("");
  };

  const applyTracklist = async () => {
    setBusy("tracklist");
    try {
      const r = await api.post(`/rip/scan/${result.scan_id}/apply-tracklist`, {
        album_id: track.album_id || "", session_id: track.album_id ? "" : track.session_id });
      toast.success(`${r.data.updated} track title${r.data.updated === 1 ? "" : "s"} applied from the photo — originals kept for undo`);
      onApplied();
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    setBusy("");
  };

  const ext = result?.extracted;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4"
      onClick={() => !busy && onClose()} data-testid="rip-scan-modal">
      <div className="w-full max-w-2xl rounded-xl border border-line bg-bg-surface p-4 sm:p-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            <ScanLine className="h-4 w-4 text-brand-cyan" /> Scan &amp; Identify CD
          </h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-primary" data-testid="rip-scan-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-ink-secondary mb-3">
          Photograph the CD with your phone — AI reads the album, artist, barcode and track listing, then finds matching
          releases. You confirm before anything is written.
        </p>

        {!result && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {SLOTS.map(([kind, label]) => (
                <div key={kind} className="rounded-lg border border-line bg-bg-elevated p-2" data-testid={`rip-scan-slot-${kind}`}>
                  <div className="text-[9px] font-mono uppercase tracking-wider text-ink-muted mb-1.5 flex items-center gap-1">
                    {kind === "barcode" ? <Barcode className="h-3 w-3" /> : kind === "tracklist" ? <ListMusic className="h-3 w-3" /> : <Camera className="h-3 w-3" />}
                    {label}
                  </div>
                  <button onClick={() => inputRefs.current[kind]?.click()}
                    data-testid={`rip-scan-pick-${kind}`}
                    className="w-full aspect-square rounded-md border border-dashed border-line hover:border-brand-cyan/60 transition overflow-hidden flex items-center justify-center bg-bg-surface">
                    {slots[kind]?.dataUrl ? (
                      <img src={slots[kind].dataUrl} alt={label} className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="h-5 w-5 text-ink-muted opacity-50" />
                    )}
                  </button>
                  {slots[kind]?.dataUrl && (
                    <div className="flex gap-1 mt-1.5">
                      <button onClick={() => rotate(kind)} data-testid={`rip-scan-rotate-${kind}`}
                        title="Rotate 90°"
                        className="flex-1 rounded border border-line py-0.5 text-[9px] font-bold hover:border-brand-amber/60 transition inline-flex items-center justify-center gap-1">
                        <RotateCw className="h-2.5 w-2.5" />
                      </button>
                      <button onClick={() => openCrop(kind)} data-testid={`rip-scan-crop-${kind}`}
                        title="Crop away the background"
                        className="flex-1 rounded border border-line py-0.5 text-[9px] font-bold hover:border-brand-amber/60 transition inline-flex items-center justify-center gap-1">
                        <Crop className="h-2.5 w-2.5" />
                      </button>
                      <button onClick={() => setSlots((s) => ({ ...s, [kind]: null }))} data-testid={`rip-scan-clear-${kind}`}
                        title="Remove photo"
                        className="rounded border border-line px-1.5 py-0.5 text-[9px] hover:text-red-400 transition">
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  )}
                  <input ref={(el) => { inputRefs.current[kind] = el; }} type="file" accept="image/*" className="hidden"
                    data-testid={`rip-scan-input-${kind}`}
                    onChange={(e) => { pick(kind, e.target.files?.[0]); e.target.value = ""; }} />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] text-ink-muted" data-testid="rip-scan-cost">
                {cost && (cost.free_for_admin
                  ? "Admin account — scans are free (usage still tracked)."
                  : `Costs ${cost.cost} credit${cost.cost === 1 ? "" : "s"} per scan — only charged when the scan succeeds. Retrying the same photos is free.`)}
              </div>
              <button onClick={scan} disabled={!filled.length || busy === "scan"} data-testid="rip-scan-run-btn"
                className="inline-flex items-center gap-2 rounded-full bg-brand-cyan/90 text-black px-4 py-2 text-xs font-bold uppercase tracking-wider hover:opacity-90 transition disabled:opacity-50">
                {busy === "scan" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4" />}
                {busy === "scan" ? "Scanning…" : `Scan & Identify${cost && !cost.free_for_admin ? ` — ${cost.cost} credit` : ""}`}
              </button>
            </div>
          </>
        )}

        {result && (
          <div className="space-y-3">
            <div className="rounded-lg border border-line bg-bg-elevated p-3" data-testid="rip-scan-extracted">
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
                What the AI read from your photos
                <span className="text-brand-cyan normal-case tracking-normal ml-2">
                  {result.cached ? "cached — no charge" : result.charged ? `${result.charged} credit used` : "no charge"}
                </span>
              </div>
              <div className="text-sm font-semibold">
                {ext?.album || <span className="text-ink-muted font-normal italic">Album title not readable</span>}
              </div>
              <div className="text-xs text-ink-muted font-mono">
                {ext?.is_compilation ? "Various Artists (compilation)" : ext?.artist || "Artist not readable"}
                {ext?.year ? ` · ${ext.year}` : ""}{ext?.label ? ` · ${ext.label}` : ""}
                {result.barcode?.value ? ` · barcode ${result.barcode.value} (${result.barcode.source})` : ""}
              </div>
              {(ext?.tracks || []).length > 0 && (
                <div className="mt-2 max-h-36 overflow-y-auto text-[11px] font-mono text-ink-secondary space-y-0.5" data-testid="rip-scan-tracks">
                  {ext.tracks.map((t, i) => (
                    <div key={i} className="truncate">{String(t.no || i + 1).padStart(2, "0")}. {t.title}{t.artist ? ` — ${t.artist}` : ""}</div>
                  ))}
                </div>
              )}
            </div>

            {(result.candidates || []).length > 0 && (
              <div className="space-y-2" data-testid="rip-scan-candidates">
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                  Matching releases — pick the correct edition
                </div>
                {result.candidates.map((c) => (
                  <div key={c.release_id} className="flex items-center gap-2.5 rounded-lg border border-line bg-bg-elevated px-3 py-2">
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold font-mono ${
                      c.confidence >= 90 ? "bg-emerald-500/20 text-emerald-300" : "bg-bg-surface text-ink-muted border border-line"}`}>
                      {c.confidence}%
                    </span>
                    <span className="relative w-10 h-10 rounded-md overflow-hidden border border-line bg-bg-surface shrink-0 flex items-center justify-center">
                      <Disc3 className="h-4 w-4 opacity-30" />
                      {c.has_cover !== false && (
                        <img src={c.cover_small} alt="" loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          className="absolute inset-0 w-full h-full object-cover" />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold truncate">{c.title}</div>
                      <div className="text-[10px] text-ink-muted font-mono truncate">
                        {c.artist}{c.year ? ` · ${c.year}` : ""}{c.country ? ` · ${c.country}` : ""} · {c.track_count} tracks
                        {result.match_basis === "barcode" ? " · barcode match" : ""}
                      </div>
                    </div>
                    <button onClick={() => applyRelease(c)} disabled={!!busy} data-testid={`rip-scan-apply-release-${c.release_id}`}
                      className="shrink-0 inline-flex items-center gap-1 rounded-full border border-brand-cyan/60 text-brand-cyan px-2.5 py-1 text-[10px] font-bold hover:bg-brand-cyan/10 transition disabled:opacity-50">
                      {busy === c.release_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                      Use this
                    </button>
                  </div>
                ))}
                <div className="text-[10px] text-ink-muted font-mono">
                  {track.album_id
                    ? "Applies metadata + Cover Art Archive artwork only to this track's album."
                    : "No album grouping — applies to ALL tracks in this session, in order."}
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-1">
              {(ext?.tracks || []).length > 0 && (
                <button onClick={applyTracklist} disabled={!!busy} data-testid="rip-scan-apply-tracklist"
                  className="inline-flex items-center gap-1.5 rounded-full border border-brand-amber/60 text-brand-amber px-3 py-1.5 text-xs font-bold hover:bg-brand-amber/10 transition disabled:opacity-50">
                  {busy === "tracklist" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListMusic className="h-3.5 w-3.5" />}
                  Apply track titles from photo
                </button>
              )}
              <button onClick={() => { setResult(null); }} data-testid="rip-scan-again-btn"
                className="rounded-full border border-line px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary transition">
                Scan different photos
              </button>
            </div>
            <div className="text-[10px] text-ink-muted">
              Nothing is changed until you apply a release or the photographed track titles. Undo anytime via Restore.
            </div>
          </div>
        )}
        {cropSlot && (
          <ImageCropModal
            file={cropSlot.blob}
            aspect={CROP_DEFAULT[cropSlot.kind] || 1}
            aspects={CROP_ASPECTS}
            title={`Crop ${SLOTS.find(([k]) => k === cropSlot.kind)?.[1]?.toLowerCase() || "photo"}`}
            maxWidth={1600}
            onCancel={() => setCropSlot(null)}
            onCropped={(blob) => {
              const r = new FileReader();
              r.onload = () => {
                setSlots((p) => ({ ...p, [cropSlot.kind]: { dataUrl: r.result } }));
                setCropSlot(null);
              };
              r.readAsDataURL(blob);
            }}
          />
        )}
      </div>
    </div>
  );
};
